import React, { ReactElement, useEffect, useState } from 'react';
import { fromNowToEndDate } from '../../../../common/utils/utils';
import { useSendGiftMutation } from '../../../../services/api/apiProvider';
import {
  ResponseGift,
  ResponseGiftBaggage,
} from '../../../../services/api/models/response/responseGift';
import { AppState, store } from '../../../../store/store';
import { AuthState, useAuth } from '../../../auth/AuthProvider';
import './GiftItem.scss';
import { ErrorResponse } from '../../../../common/constants/globalConstants';
import { useSelector } from 'react-redux';
import { LiveMatchStartEnd } from '../../../live-match/types/LiveMatchTypes';
import { reproio } from '../../../../common/utils/reproio';
import { toast } from 'react-toastify';

export interface GiftItemProps {
  gift: ResponseGift;
  giftBaggage?: ResponseGiftBaggage[];
  refetch?: () => void;
}

export const GiftItem = (props: GiftItemProps): ReactElement => {
  const { gift, giftBaggage, refetch } = props;
  const [baggage, setBaggage] = useState<ResponseGiftBaggage[] | undefined>(
    giftBaggage,
  );
  const { statusLiveMatch, liveCompetitions } = useSelector(
    (state: AppState) => state.liveMatch,
  );
  const [activeGift, setActiveGift] = useState<ResponseGiftBaggage>();
  const [giftCount, setGiftCount] = useState(0);
  const [timeLeft, setTimeLeft] = useState('');
  const [sendGift] = useSendGiftMutation();
  const { userCoin, setUserCoin } = useAuth() as AuthState;

  // Update baggage when giftBaggage prop is changed.
  useEffect(() => {
    setBaggage(giftBaggage);
  }, [giftBaggage]);

  // When baggage is changed, set active gift which has the nearest expired date.
  useEffect(() => {
    if (!baggage) {
      return;
    }
    setGiftCount(getGiftCount(baggage));
    const minExpGift = findMinExpGift(baggage);
    setTimeLeft(fromNowToEndDate(minExpGift?.expiredDateTime));
    setActiveGift(minExpGift);
  }, [baggage]);

  // Count down expired date of active gift.
  // if time out, find the next gift has min expired date.
  // if can't find gift, stop count down.
  useEffect(() => {
    if (!activeGift || !baggage) {
      return;
    }

    // set count down timer
    const timer = setInterval(() => {
      // if time's up, set the new active gift at current time.
      // if can't find gift has min expired date, stop timer.
      if (checkExpired(activeGift.expiredDateTime)) {
        const minExpGift = findMinExpGift(baggage);
        if (!minExpGift) {
          clearInterval(timer);
        }
        setActiveGift(minExpGift);
        setGiftCount(getGiftCount(baggage));
        return;
      }
      // set time left string
      setTimeLeft(fromNowToEndDate(activeGift.expiredDateTime));
    }, 1000);

    return () => {
      clearInterval(timer);
    };
  }, [activeGift]);

  /**
   * Find gift has min expired date in baggage
   * @param giftBaggage list gift baggage
   * @returns gift has min expired date
   */
  const findMinExpGift = (
    giftBaggage: ResponseGiftBaggage[],
  ): ResponseGiftBaggage | undefined => {
    if (giftBaggage.length <= 0) {
      return undefined;
    }

    let minExpGift: ResponseGiftBaggage | undefined = giftBaggage[0];
    const now = new Date();

    for (const gb of giftBaggage) {
      const minExpDate = new Date(minExpGift.expiredDateTime);
      const gbExpDate = new Date(gb.expiredDateTime);
      if (gbExpDate > now) {
        const minDiff = minExpDate.getTime() - now.getTime();
        const diff = gbExpDate.getTime() - now.getTime();
        if (minDiff < 0 || diff < minDiff) {
          minExpGift = gb;
        }
      }
    }

    const minExp = new Date(minExpGift.expiredDateTime);
    if (minExp < now) {
      minExpGift = undefined;
    }

    return minExpGift;
  };

  /**
   * Get count of gift baggage which is not expired.
   * @param giftBaggage gift baggage
   * @returns count
   */
  const getGiftCount = (giftBaggage: ResponseGiftBaggage[]): number => {
    let count = 0;
    for (const gb of giftBaggage) {
      if (!checkExpired(gb.expiredDateTime)) {
        count++;
      }
    }
    return count;
  };

  /**
   * Check reach expired time.
   * @param expAt time string
   * @returns is expired?
   */
  const checkExpired = (expAt: string): boolean => {
    const now = new Date();
    const expDate = new Date(expAt);
    return now > expDate;
  };

  const getTimeLeftColor = (): string | undefined => {
    if (!activeGift) {
      return undefined;
    }

    const now = new Date();
    const expDate = new Date(activeGift.expiredDateTime);
    const diffSecond = Math.round((expDate.getTime() - now.getTime()) / 1000);

    if (diffSecond >= 0 && diffSecond <= 10800) {
      return '#dc3545';
    } else if (diffSecond > 10800 && diffSecond <= 36000) {
      return '#ff64de';
    } else if (diffSecond > 36000) {
      return '#0b5ed7';
    } else {
      return undefined;
    }
  };

  /**
   * Handle send gift
   */
  const handleSendGift = () => {
    reproio('track', 'tap gift button');
    if (baggage) {
      sendMissionGift();
    } else {
      sendNormalGift();
    }
  };

  /**
   * Send mission gift
   */
  const sendMissionGift = async () => {
    const { liveData } = store.getState().live;
    if (liveData && baggage && activeGift) {
      // check expried date time
      if (checkExpired(activeGift.expiredDateTime)) {
        return;
      }

      // send gift via API
      const now = new Date();
      await sendGift({
        liveId: liveData.id,
        giftId: activeGift.giftId,
        liveCompetitionId:
          liveCompetitions && statusLiveMatch === LiveMatchStartEnd.START
            ? liveCompetitions.id
            : undefined,
        localDateTime: now.toISOString(),
      })
        .unwrap()
        .then(() => {
          // update new baggage, delete active item in it.
          const newBaggage = [...baggage];
          const index = newBaggage.findIndex(
            (b) => b.giftId === activeGift.giftId,
          );
          if (index >= 0) {
            newBaggage.splice(index, 1);
            setBaggage(newBaggage);
          }
        })
        .catch((error: ErrorResponse) => {
          if (error.status >= 400 && error.status < 500 && refetch) {
            refetch();
          }
        });
    }
  };

  /**
   * Send normal gift (event gift, standard gift)
   */
  const sendNormalGift = async () => {
    const { liveData } = store.getState().live;
    if (liveData && userCoin >= gift.coins) {
      const now = new Date();

      try {
        const response = await sendGift({
          liveId: liveData.id,
          giftTypeId: gift.id,
          liveCompetitionId:
            liveCompetitions && statusLiveMatch === LiveMatchStartEnd.START
              ? liveCompetitions.id
              : undefined,
          localDateTime: now.toISOString(),
        }).unwrap();

        if (response) {
          if (response.giftType.cashbackCoins) {
            const cashbackCoin = response.giftType.cashbackCoins;
            setUserCoin((userCoin) => userCoin - gift.amount + cashbackCoin);
          } else {
            setUserCoin((userCoin) => userCoin - gift.amount);
          }
        }
      } catch (error) {
        toast.error('このギフトは現在贈ることができません');
      }
    }
  };

  if (baggage && !activeGift) {
    return <></>;
  }

  return (
    <div className="gift-item" onClick={handleSendGift}>
      <div className="gift-item__top">
        <img className="gift-item__image" src={gift.icon.uri} />
        {baggage && <div className="gift-item__count">×{giftCount}</div>}
      </div>

      <div className="gift-item__name">{gift.name}</div>

      {activeGift ? (
        <div
          className="gift-item__exp-time"
          style={{ color: getTimeLeftColor() }}
        >
          {timeLeft}
        </div>
      ) : (
        <div className="gift-item__coin">{gift.amount}</div>
      )}
    </div>
  );
};
